<template>
  <!-- MBR069 Challenges — member_app_UI/song_fa_members_my_challenges_mobile -->
  <div class="page-header align-items-start min-vh-100 mbr-challenges">
    <span class="mask bg-white"></span>
    <div class="container p-0">
      <header class="mbr-challenges__bar">
        <button
          type="button"
          class="mbr-challenges__back"
          :aria-label="$t('common.back')"
          @click="goBack()"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="m15 18-6-6 6-6" />
          </svg>
        </button>
        <h1 class="mbr-challenges__title">{{ $t('challenges.title') }}</h1>
      </header>
      <main class="mbr-challenges__body" v-if="isLoadDone">
        <div v-if="campaigns.length > 0" class="pb-90">
          <article
            class="mbr-challenge"
            v-for="(campaign, index) in campaigns"
            :key="index"
          >
            <!-- MBR070 banner: campaign name + end date over the artwork -->
            <div class="mbr-challenge__banner">
              <img
                v-if="campaign.icon"
                :src="campaign.image_url"
                :alt="campaign.campaign_tile"
              />
              <span class="mbr-challenge__scrim"></span>
              <span class="mbr-challenge__live">
                <i class="mbr-challenge__dot"></i>
                {{ $t('challenges.active_campaign') }}
              </span>
              <div class="mbr-challenge__banner-foot">
                <span class="mbr-challenge__name">{{ campaign.campaign_tile }}</span>
                <span class="mbr-challenge__ends" v-if="campaign.end_date">
                  {{ $t('challenges.ends', { date: formatDate(campaign.end_date) }) }}
                </span>
              </div>
            </div>

            <div class="mbr-challenge__body">
              <div class="mbr-challenge__head">
                <h2
                  class="mbr-challenge__goal"
                  v-if="campaign.condition_type != noCondition"
                >
                  {{ goalText(campaign) }}
                </h2>
                <span class="mbr-challenge__reward">
                  {{ $t('challenges.reward_label', { type: campaign.reward_type == 1 ? $t('challenges.reward_voucher') : $t('challenges.reward_discount_code') }) }}
                </span>
              </div>

              <p class="mbr-challenge__desc">{{ descText(campaign) }}</p>

              <!-- MBR070 progress: linear bar + step ticks, replacing the radial gauge -->
              <div
                class="mbr-challenge__progress"
                v-if="campaign.condition_type != noCondition"
              >
                <div class="mbr-challenge__progress-head">
                  <span class="mbr-challenge__progress-label">{{ $t('challenges.your_progress') }}</span>
                  <span class="mbr-challenge__progress-count">
                    {{ progressText(campaign) }}
                  </span>
                </div>
                <div class="mbr-challenge__track">
                  <span
                    class="mbr-challenge__fill"
                    :style="{ width: progressPercent(campaign) + '%' }"
                  ></span>
                </div>
                <div class="mbr-challenge__steps" v-if="steps(campaign).length">
                  <span
                    v-for="step in steps(campaign)"
                    :key="step.label"
                    class="mbr-challenge__step"
                    :class="{ 'mbr-challenge__step--done': step.done }"
                  >
                    <svg
                      v-if="step.done"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="3"
                      aria-hidden="true"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {{ step.label }}
                  </span>
                </div>
              </div>

              <!-- MBR071 footer: how much is left + the claim button -->
              <div class="mbr-challenge__foot">
                <span class="mbr-challenge__hint" v-if="remainingText(campaign)">
                  {{ remainingText(campaign) }}
                </span>
                <span
                  class="mbr-challenge__issued"
                  v-if="
                    campaign.reward_type == 2 &&
                    campaign.received &&
                    !campaign.received.success
                  "
                >
                  {{ campaign.received.message }}
                </span>
                <button
                  v-else-if="checkProgress(campaign)"
                  type="button"
                  class="mbr-challenge__cta"
                  @click="getReward(campaign)"
                >
                  <span v-if="campaign.reward_type == 1 && campaign.received">
                    {{ $t('challenges.view_voucher') }}
                  </span>
                  <span v-else-if="campaign.reward_type == 2">
                    {{ $t('challenges.view_discount_code') }}
                  </span>
                  <span v-else>{{ $t('challenges.get') }}</span>
                </button>
              </div>
            </div>
          </article>
        </div>
        <!-- MBR071 empty state -->
        <section v-else class="mbr-challenges__empty">
          <span class="mbr-challenges__empty-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
              <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
              <path d="M4 22h16" />
              <path d="M10 14.66V17c0 .55-.45 1-1 1H7" />
              <path d="M14 14.66V17c0 .55.45 1 1 1h2" />
              <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
            </svg>
          </span>
          <h2 class="mbr-challenges__empty-title">{{ $t('challenges.empty_title') }}</h2>
          <p class="mbr-challenges__empty-text">
            {{ $t('challenges.empty_text') }}
          </p>
        </section>
      </main>
    </div>
    <material-loading :active="loaderActive" />
    <div class="position-fixed top-1 end-1 z-index-2">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>
    <ModalCampaignReward
      :voucherDetail="voucherDetail"
      :processExchange="processExchange"
    />
    <ModalSuccess
      :processAcction="gotoMyReward"
      :message="$t('reward.exchange_success')"
      :buttonText="$t('reward.go_to_my_reward')"
    />
    <QrcodeReward :rewardDetail="voucherDetailReward" :maxHeightModal="true"/>
    <QrcodeRewardDiscount :discountDetail="discountDetail"/>
  </div>
</template>

<script>
import { mapMutations, mapActions, mapState } from "vuex";
import CampaignService from "../services/campaign.service";
import RewardService from "../services/reward.service";
import MaterialLoading from "@/components/MaterialLoading.vue";
import ModalCampaignReward from "@/components/ModalCampaignReward.vue";
import ModalSuccess from "@/components/ModalSuccess.vue";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import QrcodeReward from "@/components/QrcodeReward.vue";
import QrcodeRewardDiscount from "@/components/QrcodeRewardDiscount.vue";
import $ from "jquery";
import { getMerchantCode } from "@/config/utils";
import { isEnglish, formatDate as formatLocaleDate } from "@/i18n";
import { errorMessage } from "@/utils/errorMessage";

export default {
  name: "my-campaigns",
  components: {
    MaterialLoading,
    ModalCampaignReward,
    ModalSuccess,
    MaterialSnackbar,
    QrcodeReward,
    QrcodeRewardDiscount
  },
  data() {
    return {
      campaigns: [],
      loaderActive: false,
      snackbar: null,
      message: "",
      isLoadDone: false,
      merchantCode: "",
      spendCondition: 1,
      purchaseCondition: 2,
      visitOfStoreCondition: 3,
      noCondition: 4,
      discountDetail: {},
      voucherDetail: {},
      voucherDetailReward: {},
      campaignIdSelect: 0,
    };
  },
  // MBR069 draws its own header like the design: hide the top navbar, and keep
  // the sidenav as an off-screen drawer (same as Booking)
  beforeMount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.add("mbr-sidenav-overlay");
    app.classList.remove("g-sidenav-pinned");
  },
  beforeUnmount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.remove("mbr-sidenav-overlay");
    if (window.innerWidth > 1200) {
      app.classList.add("g-sidenav-pinned");
    }
  },
  mounted() {
    const code = getMerchantCode();
    this.merchantCode = code ? `/${code}` : "";
    this.getCampaigns();
  },
  methods: {
    ...mapMutations(["toggleNavbarOnly", "toggleHideConfig"]),
    ...mapActions(["setHeaderColor", "setLabelColor", "setButtonColor"]),
    ...mapState(["isPinned"]),
    formatDate(date) {
      const parsed = new Date(date);
      if (isNaN(parsed)) {
        return date;
      }
      if (!isEnglish()) {
        return formatLocaleDate(parsed, { day: "2-digit", month: "short", year: "numeric" });
      }
      const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      ];
      return (
        String(parsed.getDate()).padStart(2, "0") +
        " " +
        months[parsed.getMonth()] +
        " " +
        parsed.getFullYear()
      );
    },
    formatMoney(value) {
      return (
        "$" +
        new Intl.NumberFormat("en", {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }).format(value ?? 0)
      );
    },
    // the goal the member has to reach, by condition type
    campaignGoal(campaign) {
      if (campaign.condition_type == this.spendCondition) {
        return Number(campaign.minimum_spend_amount ?? 0);
      }
      if (campaign.condition_type == this.purchaseCondition) {
        return Number(campaign.minimum_number_of_item_purchased ?? 0);
      }
      if (campaign.condition_type == this.visitOfStoreCondition) {
        return Number(campaign.number_of_stores_visit ?? 0);
      }
      return 0;
    },
    campaignTotal(campaign) {
      return Number(campaign.total ?? 0);
    },
    goalText(campaign) {
      const goal = this.campaignGoal(campaign);
      if (campaign.condition_type == this.spendCondition) {
        return this.$t("challenges.goal_spend", { amount: this.formatMoney(goal) });
      }
      if (campaign.condition_type == this.purchaseCondition) {
        return this.$t(goal == 1 ? "challenges.goal_purchase_one" : "challenges.goal_purchase_other", { n: goal });
      }
      if (campaign.condition_type == this.visitOfStoreCondition) {
        return this.$t(goal == 1 ? "challenges.goal_visit_one" : "challenges.goal_visit_other", { n: goal });
      }
      return "";
    },
    descText(campaign) {
      const isVoucher = campaign.reward_type == 1;
      if (campaign.condition_type == this.noCondition) {
        return this.$t(isVoucher ? "challenges.desc_gives_voucher" : "challenges.desc_gives_discount_code");
      }
      return this.$t(isVoucher ? "challenges.desc_complete_voucher" : "challenges.desc_complete_discount_code");
    },
    progressText(campaign) {
      const goal = this.campaignGoal(campaign);
      const total = Math.min(this.campaignTotal(campaign), goal);
      if (campaign.condition_type == this.spendCondition) {
        return this.formatMoney(total) + " / " + this.formatMoney(goal);
      }
      if (campaign.condition_type == this.purchaseCondition) {
        return this.$t(goal == 1 ? "challenges.progress_items_one" : "challenges.progress_items_other", { total, goal });
      }
      return this.$t(goal == 1 ? "challenges.progress_visits_one" : "challenges.progress_visits_other", { total, goal });
    },
    progressPercent(campaign) {
      const goal = this.campaignGoal(campaign);
      if (goal <= 0) {
        return 0;
      }
      const percent = (this.campaignTotal(campaign) / goal) * 100;
      return Math.max(0, Math.min(100, percent));
    },
    // what is still missing before the reward unlocks
    remainingText(campaign) {
      if (
        campaign.condition_type == this.noCondition ||
        this.checkProgress(campaign)
      ) {
        return "";
      }
      const left = this.campaignGoal(campaign) - this.campaignTotal(campaign);
      if (left <= 0) {
        return "";
      }
      if (campaign.condition_type == this.spendCondition) {
        return this.$t("challenges.remaining_spend", { amount: this.formatMoney(left) });
      }
      if (campaign.condition_type == this.purchaseCondition) {
        return this.$t(left == 1 ? "challenges.remaining_items_one" : "challenges.remaining_items_other", { n: left });
      }
      return this.$t(left == 1 ? "challenges.remaining_visits_one" : "challenges.remaining_visits_other", { n: left });
    },
    // step ticks only fit the countable conditions, and only while they stay readable
    steps(campaign) {
      const goal = this.campaignGoal(campaign);
      if (
        campaign.condition_type == this.spendCondition ||
        goal < 2 ||
        goal > 6
      ) {
        return [];
      }
      const stepKey =
        campaign.condition_type == this.purchaseCondition ? "challenges.step_item" : "challenges.step_visit";
      const total = this.campaignTotal(campaign);
      const list = [];
      for (let i = 1; i <= goal; i++) {
        list.push({ label: this.$t(stepKey, { n: i }), done: total >= i });
      }
      return list;
    },
    checkProgress(campaign) {
      if (campaign.condition_type == this.noCondition) {
        return true;
      }

      if (
        campaign.condition_type == this.spendCondition &&
        campaign.total >= campaign.minimum_spend_amount
      ) {
        return true;
      }

      if (
        campaign.condition_type == this.purchaseCondition &&
        campaign.total >= campaign.minimum_number_of_item_purchased
      ) {
        return true;
      }

      if (
        campaign.condition_type == this.visitOfStoreCondition &&
        campaign.total >= campaign.number_of_stores_visit
      ) {
        return true;
      }

      return false;
    },
    getCampaigns() {
      this.showLoader();
      this.isLoadDone = false;
      CampaignService.getList([]).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.campaigns = response.data.data.campaigns;
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.isLoadDone = true;
          this.hideLoader();
        },
        (error) => {
          this.isLoadDone = true;
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
        }
      );
    },
    async getReward(campaign) {
      this.showLoader();
      this.campaignIdSelect = campaign.id;
      if (campaign.reward_type == 1) {
        await RewardService.getRewardDetail(campaign.voucher_id, 0, campaign.received ? campaign.id : 0).then(
          (response) => {
            if (response.data.result.isSuccess) {
              if (campaign.received) {
                this.voucherDetailReward = response.data.data.reward;
                $(".btn-qrcode-modal").click();
              } else {
                this.voucherDetail = response.data.data.reward;
                $(".btn-campaign-modal").click();
              }
            } else {
              this.snackbar = "danger";
              this.message = errorMessage(response.data.result.message);
            }
          },
          (error) => {
            this.snackbar = "danger";
            this.message = errorMessage(error);
          }
        );
      } else {
        await CampaignService.discountDetail({
          reward_id: campaign.discount_id,
          campaign_id: campaign.id
        }).then(
          (response) => {
            if (response.data.result.isSuccess) {
              this.discountDetail = response.data.data.discount;
            } else {
              this.snackbar = "danger";
              this.message = errorMessage(response.data.result.message);
            }
          },
          (error) => {
            this.snackbar = "danger";
            this.message = errorMessage(error);
          }
        );
        $(".btn-qrcode-discount-modal").click()
      }
      this.hideLoader();
    },
    processExchange(rewardId) {
      this.closeSnackbar();
      this.showLoader();
      CampaignService.processExchange({
        reward_id: rewardId,
        campaign_id: this.campaignIdSelect,
      }).then(
        (response) => {
          if (response.data.result.isSuccess) {
            $(".btn-close").click();
            $(".btn-success-modal").click();
            this.getCampaigns();
          } else {
            $(".btn-close").click();
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
        }
      );
    },
    goBack() {
      this.$router.push(this.merchantCode + "/account");
    },
    gotoMyReward() {
      $(".btn-close").click();
      this.$router.push(this.merchantCode + "/my-rewards");
    },
    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
    closeSnackbar() {
      this.snackbar = null;
    },
    showLoader() {
      this.loaderActive = true;
    },
    hideLoader() {
      this.loaderActive = false;
    },
  },
};
</script>
<style scoped>
/* MBR069 Challenges shell */
.mbr-challenges.page-header {
  overflow: visible;
  overflow-x: clip;
  font-family: var(--mbr-font-body);
  color: var(--mbr-color-ink-headline);
}

/* custom.css still carries the legacy `* { font-family: Poppins }` */
.mbr-challenges :where(:not(.material-icons-round)) {
  font-family: var(--mbr-font-body);
}

.mbr-challenges .mask {
  background: var(--mbr-color-canvas) !important;
  opacity: 1;
}

.mbr-challenges .container {
  max-width: 440px;
  margin: 0 auto;
}

.mbr-challenges__bar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 32px 20px 12px;
  background: rgba(var(--mbr-color-canvas-rgb), 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.mbr-challenges__back {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: var(--mbr-color-surface);
  border: 1px solid var(--mbr-color-outline);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  color: var(--mbr-color-primary);
  transition: transform 0.15s ease;
}

.mbr-challenges__back:active {
  transform: scale(0.95);
}

.mbr-challenges__back svg {
  width: 18px;
  height: 18px;
}

.mbr-challenges__title {
  margin: 0;
  font-family: var(--mbr-font-headline);
  font-size: var(--mbr-headline-md-size);
  font-weight: 800;
  line-height: var(--mbr-headline-md-line);
  letter-spacing: var(--mbr-headline-md-tracking);
  color: var(--mbr-color-ink-headline);
}

.mbr-challenges__body {
  padding: 8px 20px 32px;
}

/* MBR070 campaign card */
.mbr-challenge {
  display: block;
  margin-bottom: 20px;
  padding: 16px;
  background: var(--mbr-color-card);
  border: 1px solid var(--mbr-color-tint-dark);
  border-radius: 24px;
  box-shadow: 0 8px 24px rgba(var(--mbr-color-primary-rgb), 0.06);
}

.mbr-challenge__banner {
  position: relative;
  height: 148px;
  overflow: hidden;
  background: #2c0c0c;
  border-radius: 18px;
}

.mbr-challenge__banner img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.9;
}

.mbr-challenge__scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(32, 10, 10, 0.9),
    rgba(32, 10, 10, 0.3) 55%,
    transparent
  );
}

.mbr-challenge__live {
  position: absolute;
  top: 12px;
  left: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: rgba(var(--mbr-color-surface-rgb), 0.95);
  border-radius: var(--mbr-radius-full);
  font-size: 10px;
  font-weight: var(--mbr-label-caps-weight);
  letter-spacing: var(--mbr-label-caps-tracking);
  text-transform: uppercase;
  color: var(--mbr-color-ink-headline);
}

.mbr-challenge__dot {
  width: 8px;
  height: 8px;
  border-radius: var(--mbr-radius-full);
  background: var(--mbr-color-positive);
}

.mbr-challenge__banner-foot {
  position: absolute;
  right: 12px;
  bottom: 12px;
  left: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.mbr-challenge__name {
  overflow: hidden;
  font-family: var(--mbr-font-headline);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--mbr-color-peach);
}

.mbr-challenge__ends {
  flex: none;
  font-size: 11px;
  font-weight: 500;
  color: rgba(var(--mbr-color-surface-rgb), 0.8);
}

.mbr-challenge__body {
  padding: 16px 4px 4px;
}

.mbr-challenge__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.mbr-challenge__goal {
  flex: 1;
  margin: 0;
  font-family: var(--mbr-font-headline);
  font-size: var(--mbr-headline-sm-size);
  font-weight: var(--mbr-headline-sm-weight);
  line-height: var(--mbr-headline-sm-line);
  letter-spacing: var(--mbr-headline-sm-tracking);
  color: var(--mbr-color-ink-strong);
}

.mbr-challenge__reward {
  flex: none;
  padding: 4px 10px;
  background: var(--mbr-color-peach);
  border-radius: var(--mbr-radius-full);
  font-size: 11px;
  font-weight: 700;
  color: var(--mbr-color-primary);
}

.mbr-challenge__desc {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--mbr-color-ink-secondary);
}

.mbr-challenge__progress {
  margin-top: 16px;
  padding: 14px;
  background: var(--mbr-color-canvas);
  border: 1px solid var(--mbr-color-tint-dark);
  border-radius: 16px;
}

.mbr-challenge__progress-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.mbr-challenge__progress-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--mbr-color-primary);
}

.mbr-challenge__progress-count {
  font-family: var(--mbr-font-headline);
  font-size: 12px;
  font-weight: 800;
  color: var(--mbr-color-primary);
}

.mbr-challenge__track {
  display: block;
  width: 100%;
  height: 10px;
  overflow: hidden;
  background: #eadcd9;
  border-radius: var(--mbr-radius-full);
}

.mbr-challenge__fill {
  display: block;
  height: 100%;
  background: var(--mbr-color-primary);
  border-radius: var(--mbr-radius-full);
  transition: width 0.5s ease;
}

.mbr-challenge__steps {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  margin-top: 10px;
  padding: 0 2px;
}

.mbr-challenge__step {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: var(--mbr-color-ink-muted);
}

.mbr-challenge__step--done {
  font-weight: 600;
  color: var(--mbr-color-positive);
}

.mbr-challenge__step svg {
  width: 12px;
  height: 12px;
}

.mbr-challenge__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  padding-top: 4px;
}

.mbr-challenge__hint {
  font-size: 11px;
  color: var(--mbr-color-ink-muted);
}

.mbr-challenge__issued,
.mbr-challenge__cta {
  flex: none;
  margin-left: auto;
  padding: 10px 20px;
  border: 0;
  border-radius: var(--mbr-radius-full);
  font-size: 12px;
  font-weight: 700;
}

.mbr-challenge__issued {
  background: var(--mbr-color-tint);
  color: var(--mbr-color-ink-secondary);
}

.mbr-challenge__cta {
  background: var(--mbr-color-primary);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.12);
  color: var(--mbr-color-on-primary);
  transition: background 0.15s ease, transform 0.15s ease;
}

.mbr-challenge__cta:hover {
  background: var(--mbr-color-primary-light);
}

.mbr-challenge__cta:active {
  transform: scale(0.97);
}

/* MBR071 empty state */
.mbr-challenges__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px;
  background: var(--mbr-color-card);
  border: 1px solid var(--mbr-color-tint-dark);
  border-radius: 24px;
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  text-align: center;
}

.mbr-challenges__empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
  background: var(--mbr-color-peach);
  border-radius: var(--mbr-radius-full);
  color: var(--mbr-color-primary);
}

.mbr-challenges__empty-icon svg {
  width: 32px;
  height: 32px;
}

.mbr-challenges__empty-title {
  margin: 0;
  font-family: var(--mbr-font-headline);
  font-size: 17px;
  font-weight: 700;
  color: var(--mbr-color-ink-strong);
}

.mbr-challenges__empty-text {
  max-width: 240px;
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--mbr-color-ink-secondary);
}
</style>
